import React, { useState, useCallback, useEffect } from "react";
import { Play, ArrowLeft, ArrowRight } from "lucide-react";

/**
 * LatestReleaseCarousel — centered peek-slide carousel that mirrors the Print
 * portfolio's carousel language. Prev/next slides sit at 40% opacity on either
 * side of the featured slide. Credits (Type / Director / Agency) render below
 * the media in the existing metadata format.
 */
const LatestReleaseCarousel = ({ items = [], onPlay }) => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const total = items.length;

  const go = useCallback(
    (d) => {
      if (!total) return;
      setDir(d);
      setIdx((i) => (i + d + total) % total);
    },
    [total],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  if (!total) return null;

  const current = items[idx];
  const prevIdx = (idx - 1 + total) % total;
  const nextIdx = (idx + 1) % total;
  const slideCls = dir >= 0 ? "slide-in-right" : "slide-in-left";

  return (
    <div className="w-full" data-testid="latest-release-carousel">
      {/* Centered peek carousel */}
      <div className="relative w-full flex items-center justify-center">
        {total > 1 && (
          <>
            {/* Prev peek */}
            <button
              type="button"
              aria-label="Previous release"
              onClick={() => go(-1)}
              data-testid="release-prev-peek"
              className="hidden md:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 w-[22%] rounded-2xl overflow-hidden opacity-40 hover:opacity-70 transition-opacity duration-500 z-0 bg-black aspect-video"
            >
              <img
                key={`prev-${prevIdx}`}
                src={items[prevIdx].image}
                onError={(e) => (e.currentTarget.src = items[prevIdx].imageFallback)}
                alt=""
                className={`w-full h-full object-cover ${slideCls}`}
                aria-hidden="true"
              />
            </button>
            {/* Next peek */}
            <button
              type="button"
              aria-label="Next release"
              onClick={() => go(1)}
              data-testid="release-next-peek"
              className="hidden md:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 w-[22%] rounded-2xl overflow-hidden opacity-40 hover:opacity-70 transition-opacity duration-500 z-0 bg-black aspect-video"
            >
              <img
                key={`next-${nextIdx}`}
                src={items[nextIdx].image}
                onError={(e) => (e.currentTarget.src = items[nextIdx].imageFallback)}
                alt=""
                className={`w-full h-full object-cover ${slideCls}`}
                aria-hidden="true"
              />
            </button>
          </>
        )}

        {/* Main slide */}
        <button
          type="button"
          onClick={() =>
            onPlay(current.youtubeId, {
              title: current.title,
              type: current.type,
              year: current.year,
              director: current.director,
              agency: current.agency,
            })
          }
          data-testid="release-main-slide"
          className="group relative z-10 w-full md:w-[62%] aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl shadow-black/40 text-left"
        >
          <img
            key={idx}
            src={current.image}
            onError={(e) => (e.currentTarget.src = current.imageFallback)}
            alt={current.title}
            className={`w-full h-full object-cover ${slideCls} transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]`}
          />
          <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-500" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-ink/95 text-canvas flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
              <Play className="w-5 h-5 md:w-6 md:h-6 fill-current translate-x-[1px]" />
            </span>
          </span>
        </button>
      </div>

      {/* Controls — arrows + slide counter (mirrors Print carousel) */}
      <div className="mt-6 flex items-center justify-center gap-5">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous release"
          data-testid="carousel-prev-btn"
          className="w-10 h-10 rounded-full border border-ink/25 hover:border-ink text-ink flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <span className="text-[10px] tracking-[0.32em] uppercase text-ink/55 min-w-[64px] text-center">
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next release"
          data-testid="carousel-next-btn"
          className="w-10 h-10 rounded-full border border-ink/25 hover:border-ink text-ink flex items-center justify-center transition-colors"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Metadata — same fields, same visual language as before */}
      <div className="mt-10 max-w-2xl mx-auto">
        <dl className="space-y-3 text-sm" data-testid="release-meta">
          <Meta k="Title" v={current.title} />
          <Meta k="Type" v={current.type} />
          <Meta k="Director" v={current.director} />
          <Meta k="Agency" v={current.agency} />
        </dl>
      </div>
    </div>
  );
};

const Meta = ({ k, v }) => {
  if (!v) return null;
  return (
    <div className="flex items-center justify-between border-b border-ink/10 pb-2.5">
      <dt className="text-[10px] uppercase tracking-[0.28em] text-ink/55">{k}</dt>
      <dd className="text-ink text-sm">{v}</dd>
    </div>
  );
};

export default LatestReleaseCarousel;
